import React, { ReactNode, useState } from 'react';
import { Sidebar } from './Sidebar';
import { NotificationBell } from '../NotificationBell';
import { RefreshCw } from 'lucide-react';

interface DashboardLayoutProps {
  children: ReactNode;
  onRefresh?: () => void;
  /** Page name shown in the shared slim top bar (reclaims the previously-blank left band and
   *  REPLACES each page's own big <h1>, giving ~130px of height back on every screen). */
  title?: ReactNode;
  /** Optional chip beside the title, e.g. "admin only". */
  badge?: ReactNode;
  /** Optional page-specific actions, placed left of the global refresh + notifications. */
  actions?: ReactNode;
  /** Studio needs a flush, non-scrolling body — drop the shell's default px-6 py-3 content padding. */
  flush?: boolean;
}

export function DashboardLayout({ children, onRefresh, title, badge, actions, flush }: DashboardLayoutProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);

    // Call parent's refresh handler if provided
    if (onRefresh) {
      await onRefresh();
    } else {
      // Default refresh: reload the page
      window.location.reload();
    }

    // Reset refreshing state after a short delay
    setTimeout(() => setIsRefreshing(false), 500);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto bg-background">
        <div className="sticky top-0 z-40 bg-background border-b border-border">
          <div className="container mx-auto px-6 py-2 flex items-center gap-3">
            {/* Page title fills the previously-blank left side and stands in for the per-page <h1>. */}
            {title && (
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h1>
                {badge}
              </div>
            )}
            <div className="ml-auto flex items-center gap-3">
              {actions}
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>

              <NotificationBell />
            </div>
          </div>
        </div>
        <div className={flush ? '' : 'container mx-auto px-6 py-3'}>{children}</div>
      </main>
    </div>
  );
}
